import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useSettingsStore } from './store/settingsStore';
import { supabase } from './config/supabase';
import { authService } from './services';
import ToastContainer from './components/common/ToastContainer';

import LandingPage from './pages/LandingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import GoalsPage from './pages/GoalsPage';
import PlanPage from './pages/PlanPage';
import PricingPage from './pages/PricingPage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import TransactionsPage from './pages/TransactionsPage';
import PlanProgressPage from './pages/PlanProgressPage';
import DebtPage from './pages/DebtPage';
import DebtPlanPage from './pages/DebtPlanPage';
import AppLayout from './components/layout/AppLayout';

function PrivateRoute({ children, skipOnboarding = false }) {
  const token = useAuthStore((s) => s.accessToken);
  const user = useAuthStore((s) => s.user);
  const authReady = useAuthStore((s) => s.authReady);
  if (!authReady) return null; // wait for Supabase session to initialize
  if (!token) return <Navigate to="/login" replace />;
  if (!skipOnboarding && user && !user.country) return <Navigate to="/setup" replace />;
  return children;
}

function GuestRoute({ children }) {
  const token = useAuthStore((s) => s.accessToken);
  const authReady = useAuthStore((s) => s.authReady);
  if (!authReady) return null;
  return !token ? children : <Navigate to="/dashboard" replace />;
}

export default function App() {
  const theme = useSettingsStore((s) => s.theme);
  const { setAccessToken, setUser, setAuthReady, logout } = useAuthStore();

  // Listen to Supabase auth state (login, logout, token refresh, Google OAuth callback)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session) {
          setAccessToken(session.access_token);
          if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
            // Retry sync once on 401 — fresh PKCE tokens may need a moment to
            // propagate through Supabase's network before getUser() recognises them.
            const syncWithRetry = async () => {
              try {
                return await authService.sync({});
              } catch (err) {
                if (err?.response?.status === 401) {
                  await new Promise((r) => setTimeout(r, 1500));
                  return await authService.sync({});
                }
                throw err;
              }
            };
            try {
              const user = await syncWithRetry();
              setUser(user);
            } catch (err) {
              console.error('[sync] failed after retry:', err?.response?.status, err?.message);
            }
          }
          setAuthReady();
        } else {
          logout();
          // If Supabase is still exchanging OAuth credentials (PKCE ?code= or
          // implicit #access_token=), do NOT mark auth as ready yet — PrivateRoute
          // would redirect to /login before the SIGNED_IN event fires.
          const isPKCE     = new URLSearchParams(window.location.search).has('code');
          const isImplicit = window.location.hash.includes('access_token');
          if (!isPKCE && !isImplicit) {
            setAuthReady();
          }
        }
      }
    );
    return () => subscription.unsubscribe();
  }, [setAccessToken, setUser, setAuthReady, logout]);

  useEffect(() => {
    const html = document.documentElement;
    if (theme === 'light') {
      html.classList.add('light');
      html.classList.remove('dark');
    } else {
      html.classList.remove('light');
      html.classList.add('dark');
    }
  }, [theme]);

  return (
    <BrowserRouter>
      <ToastContainer />
      <Routes>
        {/* Public */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
        <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
        <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Protected */}
        <Route
          path="/dashboard"
          element={<PrivateRoute><AppLayout><DashboardPage /></AppLayout></PrivateRoute>}
        />
        <Route
          path="/goals"
          element={<PrivateRoute><AppLayout><GoalsPage /></AppLayout></PrivateRoute>}
        />
        <Route
          path="/plans/:id"
          element={<PrivateRoute><AppLayout><PlanPage /></AppLayout></PrivateRoute>}
        />
        <Route
          path="/setup"
          element={<PrivateRoute skipOnboarding><ProfileSetupPage /></PrivateRoute>}
        />
        <Route
          path="/profile"
          element={<PrivateRoute><AppLayout><ProfilePage /></AppLayout></PrivateRoute>}
        />
        <Route
          path="/settings"
          element={<PrivateRoute><AppLayout><SettingsPage /></AppLayout></PrivateRoute>}
        />
        <Route
          path="/transactions"
          element={<PrivateRoute><AppLayout><TransactionsPage /></AppLayout></PrivateRoute>}
        />
        <Route
          path="/plans/:id/progress"
          element={<PrivateRoute><AppLayout><PlanProgressPage /></AppLayout></PrivateRoute>}
        />
        <Route
          path="/debt"
          element={<PrivateRoute><AppLayout><DebtPage /></AppLayout></PrivateRoute>}
        />
        <Route
          path="/debt/:id/plan"
          element={<PrivateRoute><AppLayout><DebtPlanPage /></AppLayout></PrivateRoute>}
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
